/*
 * Copyright (C) 2025 JPMorgan Chase & Company
 * All rights reserved.
 *
 * Redistribution and use in source and binary forms, with or without
 * modification, is not permitted.
 */

import {
  ExdsSelect,
  ExdsSelectItem,
  ExdsSelectItemText,
  ExdsValidationGroup,
  ExdsValidationItem
} from "@exds/controls";
import {ExdsSpacing} from "@exds/core";
import React, {useEffect, useState} from "react";
import PasswordInput from "../PasswordInput/PasswordInput";
import {ALPHANUMERIC_WITH_SPACES_REGEX, ERROR_ALL_FIELDS_REQUIRED} from "../../constants/constants";

export default function QuestionsSection(props) {
  const {
    questions,
    questionIndex,
    setQuestionAnswersSelected,
    questionAnswersSelected,
    formSubmitted,
    answersTouched,
    setAnswersTouched
  } = props;
  const [selectedQuestion, setSelectedQuestion] = useState();
  const [validationGroup, setValidationGroup] = useState(null);

  useEffect(() => {
    if (selectedQuestion) {
      const newQuestionAnswersSelected = questionAnswersSelected.map((questionAnswer, index) =>
        index === questionIndex ? {...questionAnswer, id: selectedQuestion.value} : questionAnswer
      );
      setQuestionAnswersSelected(newQuestionAnswersSelected);
    }
  }, [selectedQuestion]); // eslint-disable-line react-hooks/exhaustive-deps

  const handleAnswerChange = (e) => {
    const newQuestionAnswersSelected = questionAnswersSelected.map((questionAnswer, index) =>
      index === questionIndex ? {...questionAnswer, answer: e.target.value} : questionAnswer
    );
    setQuestionAnswersSelected(newQuestionAnswersSelected);

    const newAnswersTouched = [...answersTouched];
    newAnswersTouched[questionIndex] = true;
    setAnswersTouched(newAnswersTouched);
  };

  useEffect(() => {
    if (formSubmitted) {
      const sqAnswer = questionAnswersSelected[questionIndex].answer?.trim();
      const emptyAnswer = !sqAnswer;
      const incorrectNumberOfChars = !emptyAnswer && (sqAnswer.length < 3 || sqAnswer.length > 255);
      const duplicateAnswer = questionAnswersSelected.some((qa, idx) => qa.answer?.trim() === sqAnswer && idx !== questionIndex);
      const invalidAlphanumericCharactersAnswer = !emptyAnswer && !incorrectNumberOfChars && !sqAnswer.match(ALPHANUMERIC_WITH_SPACES_REGEX);

      const hasErrors = emptyAnswer || incorrectNumberOfChars || duplicateAnswer || invalidAlphanumericCharactersAnswer;

      setValidationGroup(hasErrors ? (
        <ExdsValidationGroup data-testid="input-field-validation">
          {emptyAnswer && <ExdsValidationItem status="error" text={ERROR_ALL_FIELDS_REQUIRED}/>}
          {incorrectNumberOfChars &&
            <ExdsValidationItem status="error" text='Answer must be between 3 and 255 characters in length'/>}
          {invalidAlphanumericCharactersAnswer && <ExdsValidationItem status="error" text='Only the following characters are allowed: A-Z, a-z, 0-9, space'/>}
          {duplicateAnswer && <ExdsValidationItem status="error" text='Two answers cannot be the same'/>}
        </ExdsValidationGroup>
        ) : null
      );
    }
  }, [questionAnswersSelected[questionIndex].answer, formSubmitted, answersTouched]); // eslint-disable-line react-hooks/exhaustive-deps

  return (
    <ExdsSpacing margin={{top: '1rem'}}>
      <div className="question-answers-section">
        <ExdsSelect
          labelText={`Question ${questionIndex + 1}`}
          placeholderText="Select"
          onChangeCommit={(selectedValue) => setSelectedQuestion(selectedValue[0])}
          value={selectedQuestion?.displayValue}
          alignment="left"
          density="condensed"
          labelPosition="vertical"
        >
          {questions
            .filter(
              (question) =>
                !questionAnswersSelected.some(
                  (qa, idx) => qa.id === question.id && idx !== questionIndex
                )
            )
            .map((question, index) => (
              (question.groupId === (index % 3) + 1) && (
                <div className="question-section" key={question.id}>
                  <ExdsSelectItem
                    displayValue={question.name}
                    id={index}
                    value={question.id}
                    selected={question.id === selectedQuestion?.value}
                  >
                    <ExdsSelectItemText text={question.name} />
                  </ExdsSelectItem>
                </div>
              )
            ))}
        </ExdsSelect>
        <ExdsSpacing margin={{top: '1rem'}}>
          <div className="answer-section">
            <PasswordInput
              id = {`answer-${questionIndex + 1}`}
              label={`Answer ${questionIndex + 1}`}
              value={questionAnswersSelected[questionIndex].answer}
              onChange={(e) => handleAnswerChange(e)}
              required
              validationGroup={validationGroup}
              className={validationGroup ? 'validation-error' : ''}
            />
          </div>
        </ExdsSpacing>
      </div>
    </ExdsSpacing>
  );
}
